/**
 * Kismet Quiz - Main Application
 * Complete quiz state management and UI control
 * Requires: quiz.calculator.js, quiz.validator.js
 * GA4/GTM tracking fix - 2026-05-13
 */

(function () {
 'use strict';

 const LAST_STEP_INDEX = 7;
 const KLAVIYO_QUIZ_LIST_ID = 'QSm9FF';

 // Use global getBreedSlug from quiz-main-init.js (resolved at call time since script order may vary)
 function getBreedSlug(breed) {
  if (window.getBreedSlug) return window.getBreedSlug(breed);
  if (!breed) return '';
  return breed.toLowerCase().replace(/['']/g, '_').replace(/\s+/g, '_');
 }

 // Build the Shopify CDN URL for a breed's cropped FACE avatar (used in Klaviyo email headers).
 // Strips diacritics because Shopify stores filenames without accents (e.g. "vendéen" -> "vendeen").
 function getBreedFaceUrl(breed) {
  if (!breed) return '';
  const base = window.SHOPIFY_CDN_BASE_URL || '';
  const slug = getBreedSlug(breed).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return base + 'breed_' + slug + '_face.png?width=200';
 }

 // Format dog names into a greeting string: "Rex" / "Rex & Lola" / "Rex, Lola & Max".
 function buildDogsDisplayName(names) {
  const list = names.filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return list[0] + ' & ' + list[1];
  return list.slice(0, -1).join(', ') + ' & ' + list[list.length - 1];
 }

 // Initial empty dog template
 const emptyDog = {
  name: '',
  behavior: '',
  breed: '',
  breedImgUrl: '',
  birthdayMonth: '',
  birthdayYear: 2023,
  birthdayDay: 1,
  weight: 0,
  allergies: false,
  gender: '',
  spayedOrNeutered: true,
  size: '',
  activityLevel: '',
  support: [],
  diet: '',
  // Per-dog progress tracking
  currentStep: 2, // Dogs start at step 2 (breed selection) after step 1 (names)
  completed: false
 };

 // Initial form data
 const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  zipcode: '',
  dogCount: 1,
  dogs: [{ ...emptyDog, support: [] }]
 };

 /**
  * Main Quiz Application
  */
 const KismetQuiz = {
  // State
  state: {
   currentStep: 0,
   currentDogIndex: 0,
   formData: JSON.parse(JSON.stringify(initialFormData)),
   errors: [],
   isFinished: false,
   speechText: '',
   breeds: [], // Loaded from metaobjects
   zodiacSigns: [], // Loaded from metaobjects
   activityLevels: [] // Loaded from metaobjects
  },

  /**
   * Initialize the quiz
   */
  init: function () {
   // Load saved quiz data from localStorage
   this.loadFromLocalStorage();

   // Load CMS data from Shopify metaobjects
   this.loadMetaobjects();

   // Setup event listeners
   this.setupEventListeners();

   // Render initial state
   this.render();
   this.focusFirstField();
  },

  /**
   * Load quiz data from localStorage
   */
  loadFromLocalStorage: function () {
   try {
    const saved = localStorage.getItem('kismet_quiz_data');
    const savedStep = localStorage.getItem('kismet_quiz_step');
    const savedDogIndex = localStorage.getItem('kismet_quiz_dog_index');

    // Check for URL parameters (coming from results page to complete a specific dog)
    const urlParams = new URLSearchParams(window.location.search);
    const urlDogIndex = urlParams.get('dog');
    const urlStep = urlParams.get('step');

    // Only restore state if we have saved form data
    // If no form data, always start from step 0
    if (saved) {
     this.state.formData = JSON.parse(saved);

     // Ensure dogs array has enough elements for dogCount
     const dogCount = parseInt(this.state.formData.dogCount, 10) || 1;
     while (this.state.formData.dogs.length < dogCount) {
       this.state.formData.dogs.push({ ...emptyDog, support: [] });
     }

     // Ensure all dogs have per-dog tracking fields
     this.state.formData.dogs.forEach(dog => {
       if (dog.currentStep === undefined) {
         dog.currentStep = 2; // Default to step 2 (breed selection)
       }
       if (dog.completed === undefined) {
         dog.completed = false;
       }
     });

     // Check if coming from results page with specific dog/step
     if (urlDogIndex !== null) {
       const dogIdx = parseInt(urlDogIndex, 10);
       if (dogIdx >= 0 && dogIdx < this.state.formData.dogs.length) {
         this.state.currentDogIndex = dogIdx;

         // Use URL step if provided, otherwise use dog's saved step
         // Cap at LAST_STEP_INDEX to avoid showing non-existent step for completed dogs
         if (urlStep !== null) {
           this.state.currentStep = Math.min(LAST_STEP_INDEX, Math.max(2, parseInt(urlStep, 10)));
         } else {
           const dog = this.state.formData.dogs[dogIdx];
           this.state.currentStep = Math.min(LAST_STEP_INDEX, Math.max(2, dog.currentStep || 2));
         }

         // Clean up URL params after reading
         window.history.replaceState({}, '', window.location.pathname);
         this.saveToLocalStorage();
         return;
       }
     }

     // Normal restore (no URL params)
     if (savedStep) {
      this.state.currentStep = Math.min(LAST_STEP_INDEX, parseInt(savedStep, 10));
     }

     if (savedDogIndex) {
      this.state.currentDogIndex = parseInt(savedDogIndex, 10);
     }

     // Sync global step with current dog's step (for dog-specific steps)
     // Cap at LAST_STEP_INDEX to avoid showing non-existent step for completed dogs
     if (this.state.currentStep >= 2) {
       const currentDog = this.state.formData.dogs[this.state.currentDogIndex];
       if (currentDog && currentDog.currentStep !== undefined) {
         this.state.currentStep = Math.min(LAST_STEP_INDEX, currentDog.currentStep);
       }
     }
    } else {
     // No saved data - ensure we start fresh from step 0
     this.state.currentStep = 0;
     this.state.currentDogIndex = 0;
    }
   } catch (e) {
    console.error('[Kismet Quiz] Error loading from localStorage:', e);
   }
  },

  /**
   * Save quiz data to localStorage
   */
  saveToLocalStorage: function () {
   try {
    localStorage.setItem('kismet_quiz_data', JSON.stringify(this.state.formData));
    localStorage.setItem('kismet_quiz_step', this.state.currentStep.toString());
    localStorage.setItem('kismet_quiz_dog_index', this.state.currentDogIndex.toString());
   } catch (e) {
    console.error('[Kismet Quiz] Error saving to localStorage:', e);
   }
  },

  /**
   * Load metaobjects data from the page
   * This data should be injected by Liquid in the template
   */
  loadMetaobjects: function () {
   // These will be populated by Liquid template
   if (window.quizMetaobjects) {
    this.state.breeds = window.quizMetaobjects.breeds || [];
    this.state.zodiacSigns = window.quizMetaobjects.zodiacSigns || [];
    this.state.activityLevels = window.quizMetaobjects.activityLevels || [];
   }
  },

  /**
   * Setup all event listeners
   */
  setupEventListeners: function () {
   const self = this;

   // Next button
   const nextBtn = document.getElementById('quiz-next-btn');
   if (nextBtn) {
    nextBtn.addEventListener('click', () => self.handleNext());
   }

   // Previous button
   const prevBtn = document.getElementById('quiz-prev-btn');
   if (prevBtn) {
    prevBtn.addEventListener('click', () => self.handlePrev());
   }

   // Finish button
   const finishBtn = document.getElementById('quiz-finish-btn');
   if (finishBtn) {
    finishBtn.addEventListener('click', () => self.handleFinish());
   }

   // Keyboard: Enter only advances from the last text input of step 0 (email)
   // For all other steps, user must Tab to the Next button and press Enter there.
   document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || self.state.isFinished) return;
    var el = document.activeElement;
    if (!el) return;

    // Step 0: Enter on the email field advances if valid
    if (self.state.currentStep === 0 && el.id === 'quiz-email') {
     self.handleNext();
    }
   });

   // Breadcrumb navigation
   const breadcrumbItems = document.querySelectorAll('.quiz-breadcrumb-item');
   breadcrumbItems.forEach(item => {
    item.addEventListener('click', function() {
     const targetStep = parseInt(this.dataset.targetStep, 10);
     self.handleBreadcrumbClick(targetStep);
    });
   });

   // Close button
   const closeBtn = document.getElementById('quiz-close-btn');
   if (closeBtn) {
    closeBtn.addEventListener('click', () => self.handleClose());
   }

   // Form field listeners (delegated)
   document.addEventListener('change', function (e) {
    if (e.target.classList.contains('quiz-input')) {
     self.handleFieldChange(e);
    }

    // Support checkboxes
    if (e.target.hasAttribute('data-support-option')) {
     self.handleOptionToggle('support', e.target.value);
    }
   });

   document.addEventListener('input', function (e) {
    if (e.target.classList.contains('quiz-input')) {
     self.handleFieldChange(e);
    }
   });

   // Focus trap: keep Tab cycling inside the quiz wrapper
   var wrapper = document.getElementById('quiz-wrapper');
   if (wrapper) {
    wrapper.addEventListener('keydown', function (e) {
     if (e.key !== 'Tab') return;

     // Gather all currently visible & focusable elements inside the wrapper
     var all = wrapper.querySelectorAll(
      'input:not([type="hidden"]):not([disabled]), button:not([disabled]):not([style*="display: none"]):not([style*="display:none"]), .select-selected, [tabindex="0"]'
     );
     var focusable = [];
     for (var i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null) focusable.push(all[i]);
     }
     if (focusable.length === 0) return;

     var first = focusable[0];
     var last = focusable[focusable.length - 1];

     if (e.shiftKey) {
      // Shift+Tab: if on first element, wrap to last
      if (document.activeElement === first) {
       e.preventDefault();
       last.focus();
      }
     } else {
      // Tab: if on last element, wrap to first
      if (document.activeElement === last) {
       e.preventDefault();
       first.focus();
      }
     }
    });
   }
  },

  /**
   * Handle field changes
   */
  handleFieldChange: function (event) {
   const field = event.target;
   const fieldName = field.dataset.field;
   const fieldType = field.dataset.type || 'formData';

   if (fieldType === 'formData') {
    this.state.formData[fieldName] = field.value;

    // Special handling for dog count
    if (fieldName === 'dogCount') {
       const newCount = parseInt(field.value, 10);
       const currentDogs = this.state.formData.dogs;
       
       // If increasing, add new dogs
       if (newCount > currentDogs.length) {
         const dogsToAdd = newCount - currentDogs.length;
         for (let i = 0; i < dogsToAdd; i++) {
           this.state.formData.dogs.push({ ...emptyDog, support: [] });
         }
       }
       // If decreasing, preserve data but reset currentDogIndex if out of bounds
       if (this.state.currentDogIndex >= newCount) {
         this.state.currentDogIndex = 0;
       }
    }
   } else if (fieldType === 'dog') {
    // Determine which dog index to update
    // Default to state.currentDogIndex, unless field specifies specific index (for Step 1)
    let targetIndex = this.state.currentDogIndex;
    
    if (field.dataset.dogIndex) {
       targetIndex = parseInt(field.dataset.dogIndex, 10) - 1; // 1-based in HTML
    }

    const dog = this.state.formData.dogs[targetIndex];
    if (dog) {
      if (field.type === 'checkbox') {
       dog[fieldName] = field.checked;
      } else if (field.type === 'number') {
       dog[fieldName] = parseFloat(field.value);
      } else if (fieldName === 'support') {
       // Support is stored as array, input value is comma-separated string
       dog[fieldName] = field.value ? field.value.split(',').filter(s => s) : [];
      } else if (fieldName === 'allergies' || fieldName === 'spayedOrNeutered') {
       // Convert string "true"/"false" to actual boolean
       dog[fieldName] = field.value === 'true';
      } else {
       dog[fieldName] = field.value;
      }
    }
   }

   this.saveToLocalStorage();

   // Special handling for breed change to update image immediately
   if (fieldName === 'breed') {
    this.renderDogImage();
   }

   // Clear errors when user types
   if (this.state.errors.length > 0) {
    this.state.errors = [];
    this.renderErrors();
   }
   
   // Re-render to update dependent UI (like dog selector or row visibility)
   // We invoke render mainly to update the selector if dog names change
   if (fieldName === 'name' || fieldName === 'dogCount') {
      this.render(); 
   }

   // Handle zodiac update immediately on field change
   if (this.state.currentStep === 3 && (fieldName === 'birthdayMonth' || fieldName === 'birthdayDay')) {
       this.checkZodiac();
   }

   // Handle activity level update
   if (this.state.currentStep === 5 && fieldName === 'activityLevel') {
       this.checkActivityLevel();
   }
  },

  /**
   * Sync current input values from the DOM into state before validating/advancing.
   * Browser autofill (and programmatic value-setting) updates input.value WITHOUT
   * firing 'input'/'change', so those values never reach formData via handleFieldChange.
   * Replaying the value mapping here captures them so validation and the Klaviyo payload
   * don't silently drop autofilled names/emails.
   */
  syncInputsFromDOM: function () {
   const fd = this.state.formData;
   const inputs = document.querySelectorAll('.quiz-input[data-field]');
   for (let i = 0; i < inputs.length; i++) {
    const el = inputs[i];
    if (el.offsetParent === null) continue;                    // skip hidden
    if (el.type === 'checkbox' || el.type === 'radio') continue; // toggles handled elsewhere
    const name = el.dataset.field;
    if (name === 'dogCount') continue;                          // managed by handleFieldChange (resize logic)
    const val = el.value;
    if (val === '' || val == null) continue;
    const type = el.dataset.type || 'formData';
    if (type === 'formData') {
     fd[name] = val;
    } else if (type === 'dog') {
     const idx = el.dataset.dogIndex ? (parseInt(el.dataset.dogIndex, 10) - 1) : this.state.currentDogIndex;
     const dog = fd.dogs[idx];
     if (dog) {
      if (el.type === 'number') dog[name] = parseFloat(val);
      else if (name === 'support') dog[name] = val ? val.split(',').filter(s => s) : [];
      else if (name === 'allergies' || name === 'spayedOrNeutered') dog[name] = (val === 'true');
      else dog[name] = val;
     }
    }
   }
   this.saveToLocalStorage();
  },

  /**
   * Handle multi-select options (support, diet checkboxes)
   */
  handleOptionToggle: function (fieldName, value) {
   const dog = this.state.formData.dogs[this.state.currentDogIndex];
   if (!dog) return;

   if (!Array.isArray(dog[fieldName])) {
    dog[fieldName] = [];
   }

   const index = dog[fieldName].indexOf(value);

   if (index > -1) {
    // Remove if already selected
    dog[fieldName].splice(index, 1);
   } else {
    // Add if not selected
    // If "None", clear all others
    if (value.toLowerCase() === 'none') {
     dog[fieldName] = [value];
    } else {
     dog[fieldName].push(value);
     // Remove "None" if selecting something else
     const noneIndex = dog[fieldName].findIndex(v => v.toLowerCase() === 'none');
     if (noneIndex > -1) {
      dog[fieldName].splice(noneIndex, 1);
     }
    }
   }

   this.saveToLocalStorage();
   this.render();
  },

  /**
   * Handle dog selection from footer dropdown
   */
  handleDogSelect: function(index) {
    if (index >= 0 && index < this.state.formData.dogs.length) {
        // Save current dog's progress before switching
        const previousDog = this.state.formData.dogs[this.state.currentDogIndex];
        if (previousDog && this.state.currentStep >= 2) {
            previousDog.currentStep = this.state.currentStep;
        }

        this.state.currentDogIndex = parseInt(index, 10);

        // Load the selected dog's progress
        const selectedDog = this.state.formData.dogs[this.state.currentDogIndex];
        if (selectedDog && selectedDog.currentStep !== undefined) {
            // Go to the dog's saved step (minimum step 2 for dog-specific questions)
            // Cap at LAST_STEP_INDEX to avoid showing non-existent step for completed dogs
            this.state.currentStep = Math.min(LAST_STEP_INDEX, Math.max(2, selectedDog.currentStep));
        } else {
            // New dog without progress, start at step 2
            this.state.currentStep = 2;
        }

        this.state.errors = [];
        this.saveToLocalStorage();
        this.render();
    }
  },

  /**
   * Validate current step
   */
  validate: function () {
   const currentDog = this.state.formData.dogs[this.state.currentDogIndex];
   
   // Use dogCount to determine validation scope if needed, but getQuestionErrors
   // usually validates the current step/fields.
   // Step 1 validation (Dog names) needs to check *all* active dogs.
   
   if (!window.QuizValidator) return false;
   const errors = window.QuizValidator.getQuestionErrors(
    this.state.currentStep,
    currentDog,
    this.state.formData
   );

   this.state.errors = errors;
   this.renderErrors();

   return errors.length === 0;
  },

  /**
   * Handle next button click
   */
  handleNext: async function () {
   this.syncInputsFromDOM();
   if (!this.validate()) return;

   // Send to Klaviyo (fire-and-forget — don't block quiz navigation)
   if (this.state.currentStep === 0) {
    this.sendToKlaviyo('start');
   } else {
    this.sendToKlaviyo('step');
   }

   this.state.currentStep++;
   this.state.errors = [];

   // Save per-dog progress (for steps 2+, which are dog-specific)
   if (this.state.currentStep >= 2) {
    const currentDog = this.state.formData.dogs[this.state.currentDogIndex];
    if (currentDog) {
     currentDog.currentStep = this.state.currentStep;
    }
   }

   this.saveToLocalStorage();

   // Track analytics
   this.trackAnalytics(`${this.state.currentStep + 1}`, this.state.currentStep + 1);

   // Handle zodiac calculation on birthday step
   if (this.state.currentStep === 3) {
    this.calculateAndShowZodiac();
   }

   // Handle activity level message
   if (this.state.currentStep === 5) {
    this.showActivityLevelMessage();
   }

   this.render();
   this.focusFirstField();
  },

  /**
   * Handle previous button click
   */
  handlePrev: function () {
   if (this.state.currentStep > 0) {
    this.state.currentStep--;
    this.state.errors = [];
    this.saveToLocalStorage();
    this.render();
    this.focusFirstField();
   }
  },

  /**
   * Handle finish button click
   */
  handleFinish: async function () {
   this.syncInputsFromDOM();
   if (!this.validate()) return;

   // Mark current dog as completed
   const currentDog = this.state.formData.dogs[this.state.currentDogIndex];
   if (currentDog) {
    currentDog.completed = true;
    currentDog.currentStep = LAST_STEP_INDEX + 1; // Mark as past last step
   }
   this.saveToLocalStorage();

   // Calculate recommendations for *active* dogs only
   const activeDogs = this.state.formData.dogs.slice(0, this.state.formData.dogCount);

   if (!window.QuizCalculator) { console.error('[Kismet Quiz] QuizCalculator not loaded'); return; }
   const recommendations = window.QuizCalculator.calculateAllRecommendations(
    activeDogs,
    this.state.currentDogIndex
   );

   // Generate query string
   const queryString = window.QuizCalculator.getQueryForQuizResults(recommendations);

   // Send final data to Klaviyo (fire-and-forget)
   this.sendToKlaviyo('finish', recommendations);

   // Mark as finished BEFORE tracking so quizStatus is 'completed' in the push
   this.state.isFinished = true;

   // Track completion (must come after isFinished = true)
   this.trackAnalytics('Results', LAST_STEP_INDEX + 2);

   // Show completion screen
   this.render();

   // Redirect to results after delay
   setTimeout(() => {
    window.location.href = `/pages/quiz-results${queryString}`;
   }, 2500);
  },

  /**
   * Calculate and show zodiac message
   */
  calculateAndShowZodiac: function () {
   const currentDog = this.state.formData.dogs[this.state.currentDogIndex];
   if (!window.QuizCalculator) return;
   const zodiac = window.QuizCalculator.calculateZodiacSign(currentDog);

   if (zodiac && this.state.zodiacSigns.length > 0) {
    const zodiacData = this.state.zodiacSigns.find(
     sign => sign.title.toLowerCase() === zodiac.name.toLowerCase()
    );

    if (zodiacData) {
     this.state.speechText = zodiacData.description;
     this.renderSpeechBubble();
    }
   }
  },

  /**
   * Show activity level message
   */
  showActivityLevelMessage: function () {
   const currentDog = this.state.formData.dogs[this.state.currentDogIndex];
   const activityData = this.state.activityLevels.find(
    level => level.title === currentDog.activityLevel
   );

   if (activityData) {
    this.state.speechText = activityData.description;
    this.renderSpeechBubble();
   }
  },

  /**
   * Send data to Klaviyo
   */
  /**
   * Helper to flatten dog data for Klaviyo
   * Converts nested dogs array into "dog-1_name", "dog-2_breed" etc.
   */
  flattenQuizData: function(data) {
    const flatData = {
      $email: data.email,
      $first_name: data.firstName,
      $last_name: data.lastName,
      dog_count: data.dogCount
    };

    // Add dogs data (only for active dogs based on count)
    if (data.dogs && Array.isArray(data.dogs)) {
      const activeDogs = data.dogs.slice(0, data.dogCount);
      const dogsArray = [];
      activeDogs.forEach((dog, index) => {
        const prefix = `dog_${index + 1}_`;

        // Map basic fields
        const fields = [
          'name', 'breed', 'birthdayMonth', 'birthdayDay', 'birthdayYear',
          'weight', 'gender', 'size', 'activityLevel', 'diet'
        ];

        fields.forEach(field => {
          if (dog[field] !== undefined && dog[field] !== '') {
            flatData[prefix + field] = dog[field];
          }
        });

        // Map boolean fields
        if (dog.allergies !== undefined) flatData[prefix + 'allergies'] = dog.allergies;
        if (dog.spayedOrNeutered !== undefined) flatData[prefix + 'spayedOrNeutered'] = dog.spayedOrNeutered;

        // Map array fields (support)
        if (dog.support && Array.isArray(dog.support)) {
           flatData[prefix + 'support'] = dog.support.join(',');
        }

        // Map derived fields (images)
        if (dog.breed) {
            const breedSlug = getBreedSlug(dog.breed);
            const imageUrl = (window.SHOPIFY_CDN_BASE_URL || '') + `breed_${breedSlug}.png`;
            flatData[prefix + 'breedImgUrl'] = imageUrl;
        }

        // NEW (Klaviyo dynamic email header): structured object per dog with the cropped FACE avatar.
        dogsArray.push({
          name: dog.name || '',
          breed: dog.breed || '',
          avatar_url: getBreedFaceUrl(dog.breed)
        });
      });

      // Klaviyo email-header contract: array of dogs + pre-formatted greeting + count.
      // Written ALONGSIDE the flat dog_N_* props above (additive — no breaking change for existing flows).
      flatData.dogs = dogsArray;
      flatData.dogs_display_name = buildDogsDisplayName(dogsArray.map(function (d) { return d.name; }));
    }

    return flatData;
  },

  /**
   * Send data to Klaviyo
   * Uses the new klaviyo object with fallback to legacy _learnq
   */
  sendToKlaviyo: async function (eventType, recommendationsData) {
   // Prefer new klaviyo object, fallback to _learnq
   const hasNewKlaviyo = window.klaviyo && typeof window.klaviyo.identify === 'function';
   const hasLegacyKlaviyo = window._learnq && Array.isArray(window._learnq);

   if (!hasNewKlaviyo && !hasLegacyKlaviyo) {
    console.warn('[Kismet Quiz] Klaviyo not loaded');
    return;
   }

   try {
    const flatProperties = this.flattenQuizData(this.state.formData);
    const dogCount = this.state.formData.dogCount;

    if (eventType === 'start') {
     flatProperties.is_abandoned = true;
     flatProperties.quiz_list_id = KLAVIYO_QUIZ_LIST_ID;
     flatProperties.quiz_started_at = new Date().toISOString();

     if (hasNewKlaviyo) {
      // New klaviyo object with promises (avoids race condition)
      await window.klaviyo.identify(flatProperties);
      await window.klaviyo.track('Started Quiz', { dog_count: dogCount });
     } else {
      // Legacy approach - use small delay to avoid race condition
      window._learnq.push(['identify', flatProperties]);
      await new Promise(resolve => setTimeout(resolve, 100));
      window._learnq.push(['track', 'Started Quiz', { dog_count: dogCount }]);
     }

    } else if (eventType === 'step') {
     // Mark current step completion for active dog
     flatProperties[`dog_${this.state.currentDogIndex + 1}_step_${this.state.currentStep}`] = 'completed';
     flatProperties.is_abandoned = true;
     flatProperties.quiz_last_step = this.state.currentStep;

     if (hasNewKlaviyo) {
      await window.klaviyo.identify(flatProperties);
     } else {
      window._learnq.push(['identify', flatProperties]);
     }

    } else if (eventType === 'finish') {
     flatProperties.is_abandoned = false;
     flatProperties.quiz_completed_at = new Date().toISOString();

     // Add recommendations if available
     if (recommendationsData && recommendationsData.dogs) {
      recommendationsData.dogs.forEach((rec, index) => {
       const prefix = `dog_${index + 1}_final_`;
       if (rec.recommendations) {
        flatProperties[prefix + 'product_main'] = rec.recommendations.foodFlavour;
        flatProperties[prefix + 'subscription'] = `Delivery every ${rec.recommendations.deliveryCadence} weeks`;
        flatProperties[prefix + 'bag_weight'] = rec.recommendations.bagWeightLbs;
       }
      });
     }

     if (hasNewKlaviyo) {
      await window.klaviyo.identify(flatProperties);
      await window.klaviyo.track('Completed Quiz', {
       dog_count: dogCount,
       recommendations: recommendationsData ? recommendationsData.dogs.length : 0
      });
     } else {
      window._learnq.push(['identify', flatProperties]);
      await new Promise(resolve => setTimeout(resolve, 100));
      window._learnq.push(['track', 'Completed Quiz', {
       dog_count: dogCount,
       recommendations: recommendationsData ? recommendationsData.dogs.length : 0
      }]);
     }
    }

   } catch (e) {
    console.error('[Kismet Quiz] Klaviyo error:', e);
   }
  },

  /**
   * Track Google Analytics event
   */
  trackAnalytics: function (stepName, stepNumber) {
   if (typeof dataLayer === 'undefined') return;
   dataLayer.push({
    event: 'quizStep',
    quizStepNumber: stepNumber,
    quizStepName: stepName,
    quizStatus: this.state.isFinished ? 'completed' : 'in_progress',
    quizTimestamp: new Date().toISOString()
   });
  },

  /**
   * Check dog name meaning
   */
  checkDogName: function() {
    // Show the name meaning for the currently focused dog name input.
    // Find which input is focused, or fall back to the last non-empty dog name.
    var focusedInput = document.activeElement;
    var dogIndex = 0;

    if (focusedInput && focusedInput.classList.contains('quiz-dog-name') && focusedInput.dataset.dogIndex) {
      dogIndex = parseInt(focusedInput.dataset.dogIndex, 10) - 1;
    } else {
      // Fall back: find the last dog with a name
      var dogs = this.state.formData.dogs;
      var count = parseInt(this.state.formData.dogCount, 10) || 1;
      for (var i = Math.min(count, dogs.length) - 1; i >= 0; i--) {
        if (dogs[i] && dogs[i].name && dogs[i].name.trim()) {
          dogIndex = i;
          break;
        }
      }
    }

    var dog = this.state.formData.dogs[dogIndex];
    if (!dog || !dog.name || !dog.name.trim()) {
      var hintEl = document.getElementById('quiz-dog-name-hint');
      if (hintEl) hintEl.textContent = '';
      return;
    }

    if (window.quizData && window.quizData.dogNames) {
      var nameData = window.quizData.dogNames.find(function(n) {
        return n.name.toLowerCase() === dog.name.trim().toLowerCase();
      });
      var hintElement = document.getElementById('quiz-dog-name-hint');
      if (hintElement) {
        hintElement.textContent = nameData ? nameData.response : '';
      }
    }
  },

  /**
   * Focus the first interactive field of the current step
   */
  focusFirstField: function () {
   if (this.state.isFinished) return;
   var step = document.querySelector('[data-quiz-step="' + this.state.currentStep + '"]');
   if (!step) return;

   // Find the first visible, focusable element inside the step
   var focusable = step.querySelectorAll(
    'input:not([type="hidden"]):not([disabled]), .select-selected, button:not([disabled])'
   );
   for (var i = 0; i < focusable.length; i++) {
    var el = focusable[i];
    // Skip hidden elements (e.g. dog rows that are display:none)
    if (el.offsetParent !== null) {
     el.focus();
     return;
    }
   }
  },

  /**
   * Render the current UI state
   */
  render: function () {
   this.renderSteps();
   this.renderProgressBar();
   this.renderButtons();
   this.renderErrors();
   this.populateFields();
   this.renderDogImage();
   this.renderBreadcrumb();

   if (this.state.currentStep === 1) {
       this.checkDogName();
   }

   if (this.state.currentStep === 3) {
       this.checkZodiac();
   }

   if (this.state.currentStep === 5) {
       this.checkActivityLevel();
   }

   this.renderSpeechBubble();
   this.renderDogSelector();
  },

  /**
   * Show/hide quiz steps
   */
  renderSteps: function () {
   const steps = document.querySelectorAll('[data-quiz-step]');
   steps.forEach(step => {
    const stepNum = parseInt(step.dataset.quizStep, 10);
    step.style.display = stepNum === this.state.currentStep ? 'block' : 'none';
   });

   // Show/hide completion screen
   const completionScreen = document.getElementById('quiz-completion');
   if (completionScreen) {
    completionScreen.style.display = this.state.isFinished ? 'block' : 'none';

    // Populate dog name and illustration when showing completion screen
    if (this.state.isFinished && this.state.formData.dogs && this.state.formData.dogs[this.state.currentDogIndex]) {
     const dog = this.state.formData.dogs[this.state.currentDogIndex];

     // Update dog name in title
     const completionDogName = document.getElementById('completion-dog-name');
     if (completionDogName && dog.name) {
      completionDogName.textContent = dog.name;
     }

     // Update dog name in text
     const completionDogNameInline = document.getElementById('completion-dog-name-inline');
     if (completionDogNameInline && dog.name) {
      completionDogNameInline.textContent = dog.name;
     }

     // Update dog illustration with fade-in
     const completionDogIllustration = document.getElementById('completion-dog-illustration');
     if (completionDogIllustration) {
      const breedSlug = getBreedSlug(dog.breed);
      const imageUrl = (window.SHOPIFY_CDN_BASE_URL || '') + `breed_${breedSlug}.png`;
      if (typeof window.loadDogImageWithTransition === 'function') {
        window.loadDogImageWithTransition(completionDogIllustration, imageUrl, dog.breed, true);
      } else {
        completionDogIllustration.innerHTML = `<img src="${imageUrl}" alt="${dog.breed}" onerror="this.style.display='none'"/>`;
      }
     }
    }
   }

   const quizContainer = document.getElementById('quiz-container');
   if (quizContainer) {
    quizContainer.style.display = this.state.isFinished ? 'none' : 'block';
   }
  },

  /**
   * Update progress bar
   */
  renderProgressBar: function () {
   const progressBar = document.getElementById('quiz-progress-bar');
   if (progressBar) {
    const progress = ((this.state.currentStep + 1) / (LAST_STEP_INDEX + 1)) * 100;
    progressBar.style.width = progress + '%';
   }

   const progressText = document.getElementById('quiz-progress-text');
   if (progressText) {
    progressText.textContent = `Step ${this.state.currentStep + 1} of ${LAST_STEP_INDEX + 1}`;
   }
  },

  /**
   * Update button states
   */
  renderButtons: function () {
   const nextBtn = document.getElementById('quiz-next-btn');
   const prevBtn = document.getElementById('quiz-prev-btn');
   const finishBtn = document.getElementById('quiz-finish-btn');

   if (prevBtn) {
    prevBtn.disabled = this.state.currentStep === 0;
   }

   const isLastStep = this.state.currentStep === LAST_STEP_INDEX;

   if (nextBtn) {
    nextBtn.style.display = isLastStep ? 'none' : 'inline-block';
   }

   if (finishBtn) {
    finishBtn.style.display = isLastStep ? 'inline-block' : 'none';
   }
  },

  /**
   * Check and display zodiac sign traits
   */
  checkZodiac: function() {
    const dog = this.state.formData.dogs[this.state.currentDogIndex];
    if (!dog || !dog.birthdayMonth || !dog.birthdayDay) return;

    // Use calculator to find sign
    if (!window.QuizCalculator) return;
    const zodiac = window.QuizCalculator.calculateZodiacSign(dog);

    const speechBubble = document.getElementById('quiz-dog-speech-3');
    if (!speechBubble) return;

    if (zodiac && this.state.zodiacSigns) {
        // Find traits in metaobjects
        const signData = this.state.zodiacSigns.find(s => s.title.toLowerCase() === zodiac.name.toLowerCase());
        
        if (signData) {
            speechBubble.textContent = signData.description;
            speechBubble.style.opacity = '1';
        } else {
            speechBubble.style.opacity = '0';
        }
    } else {
        speechBubble.style.opacity = '0';
    }
  },

  /**
   * Check and display activity level traits
   */
  checkActivityLevel: function() {
     const dog = this.state.formData.dogs[this.state.currentDogIndex];
     if (!dog || !dog.activityLevel) return;

     const speechBubble = document.getElementById('quiz-dog-speech-5');
     if (!speechBubble) return;

     if (this.state.activityLevels) {
         // Find description in metaobjects
         const activityData = this.state.activityLevels.find(s => s.title.toLowerCase() === dog.activityLevel.toLowerCase());
         
         if (activityData) {
             speechBubble.textContent = activityData.description;
             speechBubble.style.opacity = '1';
         } else {
             speechBubble.style.opacity = '0';
         }
     }
  },

  /**
   * Render mini breadcrumb navigation
   */
  renderBreadcrumb: function() {
    const breadcrumb = document.getElementById('quiz-breadcrumb');
    if (!breadcrumb) return;

    // Hide breadcrumb when finished
    if (this.state.isFinished) {
      breadcrumb.style.display = 'none';
      return;
    }
    breadcrumb.style.display = 'flex';

    const items = breadcrumb.querySelectorAll('.quiz-breadcrumb-item');
    const currentStep = this.state.currentStep;

    items.forEach(item => {
      const section = item.dataset.section;
      let isActive = false;

      if (section === 'you' && currentStep === 0) {
        isActive = true;
      } else if (section === 'dog' && currentStep >= 1 && currentStep <= 4) {
        isActive = true;
      } else if (section === 'traits' && currentStep >= 5 && currentStep <= 7) {
        isActive = true;
      }

      item.classList.toggle('active', isActive);
    });
  },

  /**
   * Handle breadcrumb navigation click
   */
  handleBreadcrumbClick: function(targetStep) {
    // Only allow navigating to steps that have been reached
    const currentDog = this.state.formData.dogs[this.state.currentDogIndex];
    const maxReachedStep = currentDog ? (currentDog.currentStep || 0) : 0;

    // For step 0 (YOU), always allow if we have data
    // For dog steps (1-4), allow if we've passed step 0
    // For traits steps (5-7), allow if we've reached step 5
    let canNavigate = false;

    if (targetStep === 0) {
      canNavigate = true; // Always can go back to step 0
    } else if (targetStep >= 1 && targetStep <= 4) {
      canNavigate = this.state.currentStep >= 1 || maxReachedStep >= 1;
    } else if (targetStep >= 5) {
      canNavigate = this.state.currentStep >= 5 || maxReachedStep >= 5;
    }

    if (canNavigate) {
      this.state.currentStep = targetStep;
      this.state.errors = [];
      this.saveToLocalStorage();
      this.render();
      this.focusFirstField();
    }
  },

  /**
   * Handle close button click - return to previous page or home
   */
  handleClose: function() {
    // Check if there's a referrer from the same origin
    const referrer = document.referrer;
    const currentOrigin = window.location.origin;

    if (referrer && referrer.startsWith(currentOrigin)) {
      // Don't go back to the quiz itself or results page
      const isQuizPage = referrer.includes('/quiz') || referrer.includes('view=quiz');
      if (!isQuizPage) {
        window.location.href = referrer;
        return;
      }
    }

    // Fallback to home
    window.location.href = '/';
  },

  /**
   * Render dog selector in footer
   */
  renderDogSelector: function() {
    // Log state for debugging
    const dogCount = parseInt(this.state.formData.dogCount, 10);
    const currentStep = this.state.currentStep;
    const isFinished = this.state.isFinished;
    
    // Only show if more than 1 dog and we are past step 1 (where we define dogs)
    const showSelector = dogCount > 1 && currentStep > 1 && !isFinished;
    
    // Find or create container (we will create it in liquid, but here we populate it)
    let container = document.getElementById('quiz-dog-selector-container');
    
    // Fallback: Try to find the footer and append if missing (though it should be there)
    if (!container) {
        const footer = document.querySelector('.quiz-footer');
        if (footer) {
            container = document.createElement('div');
            container.id = 'quiz-dog-selector-container';
            container.style.display = 'none';
            // Insert before the next button (2nd button usually)
            const nextBtn = document.getElementById('quiz-next-btn');
            footer.insertBefore(container, nextBtn);
        } else {
             console.warn('[Kismet Quiz] Selector container AND footer not found!');
             return;
        }
    }
    
    if (showSelector) {
        container.style.display = 'block';
        
        // Build selector HTML with custom select style if possible, or standard for now
        // Using standard select for simplicity as 'custom-select' requires re-init
        // or we manually build the custom structure.
        // Let's rely on standard select and let css style it, or try to rebuild.
        
        let options = '';
        for(let i = 0; i < this.state.formData.dogCount; i++) {
            const dog = this.state.formData.dogs[i];
            const selected = i === this.state.currentDogIndex ? 'selected' : '';
            options += `<option value="${i}" ${selected}>${dog.name || 'Dog ' + (i+1)}</option>`;
        }
                // We use a native select styled to match the theme, as dynamic custom-select 
            // initialization is complex to handle here without duplication.
            
            container.innerHTML = `
                <div class="quiz-dog-selector-wrapper" style="position: relative; width: 200px;">
                    <select 
                        onchange="window.KismetQuiz.handleDogSelect(this.value)"
                        style="
                            width: 100%;
                            padding: 0.5rem 2rem 0.5rem 1rem;
                            border: 2px solid #fbf1e4;
                            border-radius: 9999px;
                            background-color: #036356;
                            color: #fbf1e4;
                            font-family: 'Apercu', sans-serif;
                            font-size: 18px;
                            cursor: pointer;
                            appearance: none;
                            -webkit-appearance: none;
                            background-image: url('${window.SHOPIFY_CDN_BASE_URL ? window.SHOPIFY_CDN_BASE_URL.replace('files/', 't/1/assets/') : ''}select-dropdown-arrow.svg'); 
                            /* Note: asset URL handling in JS is tricky, using simple arrow fallback or color */
                        "
                    >
                        ${options}
                    </select>
                    <div style="
                        position: absolute;
                        top: 50%;
                        right: 1rem;
                        transform: translateY(-50%);
                        pointer-events: none;
                        border-left: 6px solid transparent;
                        border-right: 6px solid transparent;
                        border-top: 6px solid #fbf1e4;
                    "></div>
                </div>
            `;
        
        // Re-init custom selects logic if global function available
        if (typeof window.initCustomSelects === 'function') {
            // We might need to handle cleanup first if we keep re-rendering
            // For now, simpler implementation:
            // Just use the native select functionality via inline onchange above
            // If we want the *visual* custom select, we need to call initCustomSelects() 
            // but limited to this element to avoid double binding.
            // Assuming initCustomSelects handles "already initialized" checks or we just clear and rebuild.
            // Actually `initCustomSelects` iterates `document.getElementsByClassName('custom-select')`.
            // It might double-wrap if we are not careful.
            // So I won't call it globally. Use native select for now or implement safe init.
        }
    } else {
        container.style.display = 'none';
    }
  },

  /**
   * Show/hide error messages
   */
  renderErrors: function () {
    // Find the error container for the current visible step
    const currentStepEl = document.querySelector(`.quiz-step[data-quiz-step="${this.state.currentStep}"]`);
    if (!currentStepEl) return;

    const errorContainer = currentStepEl.querySelector('.quiz-step-errors');
    // If no specific container found, retry finding global one (backwards compatibility) or return
    if (!errorContainer) return;

    if (this.state.errors.length === 0) {
      errorContainer.style.display = 'none';
      errorContainer.innerHTML = '';
      errorContainer.classList.remove('has-errors');
    } else {
      errorContainer.style.display = 'block';
      errorContainer.classList.add('has-errors');
      errorContainer.innerHTML = '<p class="quiz-error-message">' +
        this.state.errors.join('<br>') + '</p>';
    }
  },

  /**
   * Populate form fields with current data
   */
  populateFields: function () {
   // Populate user info fields
   const firstNameField = document.querySelector('[data-field="firstName"]');
   if (firstNameField) firstNameField.value = this.state.formData.firstName || '';

   const lastNameField = document.querySelector('[data-field="lastName"]');
   if (lastNameField) lastNameField.value = this.state.formData.lastName || '';

   const emailField = document.querySelector('[data-field="email"]');
   if (emailField) emailField.value = this.state.formData.email || '';

   const zipcodeField = document.querySelector('[data-field="zipcode"]');
   if (zipcodeField) zipcodeField.value = this.state.formData.zipcode || '';

   // Populate dogCount and update row visibility
   const dogCountField = document.getElementById('quiz-dog-count');
   const dogCount = parseInt(this.state.formData.dogCount, 10) || 1;
   if (dogCountField) {
     dogCountField.value = dogCount;

     // Update custom select visual
     const wrapper = dogCountField.closest('.custom-select');
     if (wrapper) {
       const selectedDisplay = wrapper.querySelector('.select-selected');
       if (selectedDisplay && dogCountField.options && dogCountField.selectedIndex >= 0) {
         selectedDisplay.innerHTML = dogCountField.options[dogCountField.selectedIndex].innerHTML;
       }
     }

     // Show/hide dog rows based on count
     for (let i = 1; i <= 3; i++) {
       const row = document.getElementById(`dog-row-${i}`);
       if (row) {
         row.style.display = i <= dogCount ? 'flex' : 'none';
       }
     }

     // Update label text (dog vs dogs)
     const dogLabel = document.getElementById('dog-label');
     if (dogLabel) {
       dogLabel.textContent = dogCount > 1 ? 'dogs, named' : 'dog, named';
     }
   }

   // Populate Step 1 dog-specific fields (name and behavior for each dog row)
   const indexedDogFields = document.querySelectorAll('[data-dog-index]');
   const focused = document.activeElement;
   indexedDogFields.forEach(field => {
    const dogIndex = parseInt(field.dataset.dogIndex, 10) - 1; // Convert 1-based to 0-based
    const fieldName = field.dataset.field;
    const dog = this.state.formData.dogs[dogIndex];

    if (dog && fieldName && dog[fieldName] !== undefined) {
      // Skip setting value on the focused input to preserve cursor position
      if (field === focused && field.tagName === 'INPUT') return;
      field.value = dog[fieldName];

      // For custom selects (behavior), update the visual display
      const wrapper = field.closest('.custom-select');
      if (wrapper) {
        const selectedDisplay = wrapper.querySelector('.select-selected');
        if (selectedDisplay && field.options && field.selectedIndex >= 0) {
          selectedDisplay.innerHTML = field.options[field.selectedIndex].innerHTML;

          // Update 'same-as-selected' class in dropdown
          const items = wrapper.querySelectorAll('.select-items div');
          items.forEach(item => {
            if (item.innerHTML === field.options[field.selectedIndex].innerHTML) {
              item.classList.add('same-as-selected');
            } else {
              item.classList.remove('same-as-selected');
            }
          });
        }
      }
    }
   });

   // Populate current dog fields (for steps 2+)
   const currentDog = this.state.formData.dogs[this.state.currentDogIndex];
   if (!currentDog) return;

   const dogFields = document.querySelectorAll('[data-type="dog"]');
   dogFields.forEach(field => {
    const fieldName = field.dataset.field;

    // Skip fields that are for specific dogs in Step 1 (already handled above)
    if (field.dataset.dogIndex) return;

    if (fieldName && currentDog[fieldName] !== undefined) {
      if (field.type === 'checkbox') {
       field.checked = currentDog[fieldName];
      } else {
       field.value = currentDog[fieldName];
      }
      
      // For custom selects, we need to update the visual display
      // This is done loosely in quiz-main-init.js with timeouts, but we should do it here if possible.
      // Triggering 'change' might cause loops if not careful? 
      // No, we just want to update the visual 'select-selected'.
      // Let's try finding the wrapper and updating it.
      const wrapper = field.closest('.custom-select');
      if (wrapper) {
        const selectedDisplay = wrapper.querySelector('.select-selected');
        if (selectedDisplay && field.options && field.selectedIndex >= 0) {
           selectedDisplay.innerHTML = field.options[field.selectedIndex].innerHTML;
           
           // Also update class 'same-as-selected' in the list
           const items = wrapper.querySelectorAll('.select-items div');
           items.forEach((item, idx) => {
               if(item.innerHTML === field.options[field.selectedIndex].innerHTML) {
                   item.classList.add('same-as-selected');
               } else {
                   item.classList.remove('same-as-selected');
               }
           });
        }
      }
    }
   });

   // Populate support chips and hidden input
   const supportChips = document.querySelectorAll('.quiz-chip[data-support]');
   const supportInput = document.getElementById('quiz-dog-support');
   const supportArray = currentDog.support || [];

   // Update hidden input with current dog's support values
   if (supportInput) {
     supportInput.value = Array.isArray(supportArray) ? supportArray.join(',') : supportArray;
   }

   // Update visual state of chips
   supportChips.forEach(chip => {
     const val = chip.dataset.support;
     const isSelected = Array.isArray(supportArray)
       ? supportArray.includes(val)
       : supportArray === val;

     if (isSelected) {
       chip.classList.add('selected');
     } else {
       chip.classList.remove('selected');
     }
   });

   // Populate diet chips and hidden input (single-select)
   const dietChips = document.querySelectorAll('.quiz-chip[data-diet]');
   const dietInput = document.getElementById('quiz-dog-diet');
   const dietValue = currentDog.diet || '';

   // Update hidden input with current dog's diet value
   if (dietInput) {
     dietInput.value = dietValue;
   }

   // Update visual state of chips (single-select)
   dietChips.forEach(chip => {
     const val = chip.dataset.diet;
     if (val === dietValue) {
       chip.classList.add('selected');
     } else {
       chip.classList.remove('selected');
     }
   });

   // Populate size options and hidden input
   const sizeOptions = document.querySelectorAll('.quiz-size-option');
   const sizeInput = document.getElementById('quiz-dog-size');
   const sizeValue = currentDog.size || '';

   // Update hidden input with current dog's size value
   if (sizeInput) {
     sizeInput.value = sizeValue;
   }

   // Update visual state of size options
   sizeOptions.forEach(option => {
     const val = option.dataset.size;
     if (val === sizeValue) {
       option.classList.add('selected');
     } else {
       option.classList.remove('selected');
     }
   });

   // Update name displays across steps
   const nameDisplays = document.querySelectorAll('[id^="dog-name-display"], [id^="quiz-dog-title"]');
   nameDisplays.forEach(el => {
        // Check if it's the completion title (handled separately) or specific step titles
        if(el.id === 'completion-dog-name' || el.id === 'completion-dog-name-inline') return;
        
        // Only update if it's generic or matches current dog context
        // Actually, all "Your Dog" titles in steps should match current dog
        if (!el.id.includes('completion')) {
            // For Step 1 specific inputs, they are static.
            // For Step 2+, they refer to current dog.
            el.textContent = currentDog.name || 'Your Dog';
        }
   });

   // Show/hide step 2 illustration based on whether breed is selected
   const step2Divider = document.getElementById('quiz-step2-divider');
   const step2Illustration = document.getElementById('quiz-step2-illustration');
   if (currentDog.breed) {
     if (step2Divider) step2Divider.classList.remove('quiz-step2-hidden');
     if (step2Illustration) step2Illustration.classList.remove('quiz-step2-hidden');
   } else {
     if (step2Divider) step2Divider.classList.add('quiz-step2-hidden');
     if (step2Illustration) step2Illustration.classList.add('quiz-step2-hidden');
   }
  },

  /**
   * Update dog breed image
   */
  renderDogImage: function () {
   const currentDog = this.state.formData.dogs[this.state.currentDogIndex];
   if (!currentDog) {
     return;
   }

   // Update all illustration containers for steps 2-7
   const containers = document.querySelectorAll('.quiz-dog-illustration');

   containers.forEach((container) => {
     if (!currentDog.breed) {
       return;
     }

     const breedSlug = getBreedSlug(currentDog.breed);
     const filename = `breed_${breedSlug}.png`;
     const baseUrl = window.SHOPIFY_CDN_BASE_URL || '';
     const imageUrl = `${baseUrl}${filename}`;

     if (typeof window.loadDogImageWithTransition === 'function') {
       window.loadDogImageWithTransition(container, imageUrl, currentDog.breed, true);
     } else {
       container.innerHTML = `<img src="${imageUrl}" alt="${currentDog.breed}" class="quiz-dog-illustration-img" onerror="this.style.display='none'"/>`;
     }
   });
  },

  /**
   * Show speech bubble with message
   */
  renderSpeechBubble: function () {
   const speechBubble = document.getElementById('quiz-speech-bubble');
   if (!speechBubble) return;

   if (this.state.speechText) {
    speechBubble.textContent = this.state.speechText;
    speechBubble.style.display = 'block';
   } else {
    speechBubble.style.display = 'none';
   }
  }
 };

 // Initialize quiz when DOM is ready
 if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => KismetQuiz.init());
 } else {
  KismetQuiz.init();
 }

 // Expose globally for debugging
 window.KismetQuiz = KismetQuiz;

})();
